import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { ArrowLeft, Mail, Zap, Clock, Shield, AlertTriangle, CheckCircle2, Send } from "lucide-react";

/**
 * Email automation settings (Phase 1).
 * - SMTP connection (Gmail/Outlook manual, or any SMTP)
 * - Tone + delay + daily cap + active hours + auto-channels
 * - Signature (HTML)
 * - Master enable toggle with legal disclaimer
 */
export default function EmailSettings() {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  // Local editable state
  const [smtp, setSmtp] = useState({
    smtp_host: "", smtp_port: 587, smtp_user: "", smtp_pass: "",
    smtp_starttls: true, from_email: "", from_name: "",
  });
  const [rules, setRules] = useState({
    tone: "Semi-formel", delay_minutes: 5, daily_cap: 20,
    active_hours_start: 8, active_hours_end: 18,
    active_days: [0, 1, 2, 3, 4],
    auto_channels: { visit: true, phone: true, linkedin: false, whatsapp: false },
    signature_html: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get("/emails/config");
      setCfg(data);
      setSmtp((s) => ({
        ...s,
        smtp_host: data.smtp_host || "",
        smtp_port: data.smtp_port || 587,
        smtp_user: data.smtp_user || "",
        smtp_starttls: data.smtp_starttls !== false,
        from_email: data.from_email || "",
        from_name: data.from_name || "",
        smtp_pass: "",
      }));
      setRules((r) => ({
        ...r,
        tone: data.tone || "Semi-formel",
        delay_minutes: data.delay_minutes ?? 5,
        daily_cap: data.daily_cap ?? 20,
        active_hours_start: data.active_hours_start ?? 8,
        active_hours_end: data.active_hours_end ?? 18,
        active_days: data.active_days || [0, 1, 2, 3, 4],
        auto_channels: data.auto_channels || r.auto_channels,
        signature_html: data.signature_html || "",
      }));
    } catch {
      toast.error("Impossible de charger la configuration");
    }
  };
  useEffect(() => { load(); }, []);

  const saveSMTP = async () => {
    if (!smtp.smtp_host || !smtp.smtp_user || !smtp.from_email) {
      toast.error("Hôte, utilisateur et email d'envoi sont requis");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...smtp };
      if (!payload.smtp_pass) delete payload.smtp_pass;  // keep previous
      await api.put("/emails/smtp", payload);
      toast.success("SMTP enregistré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur SMTP");
    } finally { setSaving(false); }
  };

  const saveRules = async () => {
    setSaving(true);
    try {
      await api.put("/emails/rules", rules);
      toast.success("Règles enregistrées");
      await load();
    } catch { toast.error("Erreur enregistrement"); }
    finally { setSaving(false); }
  };

  const testSmtp = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/emails/test");
      toast.success(`Email test envoyé à ${data.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test SMTP échoué");
    } finally { setTesting(false); }
  };

  const toggleEnabled = async () => {
    if (!cfg?.smtp_password_set) {
      toast.error("Configurez d'abord votre SMTP avant d'activer");
      return;
    }
    if (!cfg?.enabled) {
      setShowDisclaimer(true);
      return;
    }
    // Disable
    await api.put("/emails/rules", { enabled: false });
    toast.success("Envoi automatique désactivé");
    await load();
  };

  const confirmEnable = async () => {
    await api.put("/emails/rules", { enabled: true });
    toast.success("Envoi automatique activé");
    setShowDisclaimer(false);
    await load();
  };

  if (!cfg) {
    return <AppShell><div className="p-6 text-sm text-zinc-400">Chargement…</div></AppShell>;
  }

  return (
    <AppShell>
      {/* Header */}
      <div className="px-5 pt-6 pb-2 flex items-center gap-3">
        <button onClick={() => navigate("/settings")} className="w-9 h-9 rounded-full border border-[var(--border)] grid place-items-center hover:bg-black/5" data-testid="email-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[var(--text)] tracking-tight leading-[0.95]" data-testid="email-settings-title">
          Envoi automatique
        </h1>
        <span className="ml-2 text-[10px] font-bold uppercase tracking-widest bg-orange-500 text-white px-2 py-0.5 rounded-full">Beta</span>
      </div>
      <p className="px-5 mt-3 text-sm text-zinc-400 max-w-2xl">
        Prosperr rédige et envoie automatiquement les emails de relance après chaque visite commerciale, directement depuis <strong>votre adresse email professionnelle</strong>. Vous validez les paramètres et restez maître de chaque envoi.
      </p>

      {/* Master toggle */}
      <div className="px-5 mt-6 max-w-2xl">
        <div className={`border rounded-2xl p-5 ${cfg.enabled ? "border-emerald-500 bg-emerald-500 text-white" : "border-[var(--border)] bg-[var(--surface)]"}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full grid place-items-center ${cfg.enabled ? "bg-white/10" : "bg-[var(--surface-alt)]"}`}>
              <Zap className={`w-4 h-4 ${cfg.enabled ? "text-white" : "text-zinc-400"}`} />
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg">{cfg.enabled ? "Envoi automatique activé" : "Envoi automatique désactivé"}</div>
              <div className={`text-xs ${cfg.enabled ? "text-white/70" : "text-zinc-400"}`}>
                {cfg.enabled ? `${cfg.daily_cap || 20} emails max par jour, ${cfg.delay_minutes} min avant envoi.` : "Activez pour envoyer automatiquement les emails après vos visites."}
              </div>
            </div>
            <Switch checked={!!cfg.enabled} onCheckedChange={toggleEnabled} data-testid="email-toggle-enabled" />
          </div>
        </div>
      </div>

      {/* SMTP config */}
      <div className="px-5 mt-6 max-w-2xl">
        <h2 className="font-display font-bold text-xl">1. Connexion email (SMTP)</h2>
        <p className="text-xs text-zinc-400 mt-1">Gmail OAuth arrive bientôt. Pour l'instant, renseignez les infos SMTP (mot de passe d'application pour Gmail).</p>
        <div className="mt-4 border border-[var(--border)] p-5 rounded-2xl space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Serveur</label>
              <Input value={smtp.smtp_host} onChange={(e) => setSmtp({ ...smtp, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" className="h-11 rounded-md border-[var(--border)]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Port</label>
              <Input type="number" value={smtp.smtp_port} onChange={(e) => setSmtp({ ...smtp, smtp_port: +e.target.value })} data-testid="smtp-port" className="h-11 rounded-md border-[var(--border)]" />
            </div>
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Utilisateur</label>
            <Input value={smtp.smtp_user} onChange={(e) => setSmtp({ ...smtp, smtp_user: e.target.value })} placeholder="vous@votre-domaine.fr" data-testid="smtp-user" className="h-11 rounded-md border-[var(--border)]" />
          </div>
          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Mot de passe {cfg.smtp_password_set && "(déjà configuré, laisser vide pour conserver)"}</label>
            <Input type="password" value={smtp.smtp_pass} onChange={(e) => setSmtp({ ...smtp, smtp_pass: e.target.value })} placeholder={cfg.smtp_password_set ? "••••••••" : "mot de passe d'application"} data-testid="smtp-pass" className="h-11 rounded-md border-[var(--border)]" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Email d'envoi</label>
              <Input value={smtp.from_email} onChange={(e) => setSmtp({ ...smtp, from_email: e.target.value })} placeholder="vous@votre-domaine.fr" data-testid="smtp-from-email" className="h-11 rounded-md border-[var(--border)]" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Nom affiché</label>
              <Input value={smtp.from_name} onChange={(e) => setSmtp({ ...smtp, from_name: e.target.value })} placeholder="Thomas Martin" data-testid="smtp-from-name" className="h-11 rounded-md border-[var(--border)]" />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button onClick={saveSMTP} disabled={saving} className="rounded-full bg-black text-white h-11 px-5 text-sm" data-testid="smtp-save">
              {saving ? "…" : "Enregistrer"}
            </Button>
            <Button onClick={testSmtp} disabled={testing || !cfg.smtp_password_set} variant="outline" className="rounded-full border-[var(--border)] h-11 px-5 text-sm" data-testid="smtp-test">
              {testing ? "Test en cours…" : "Tester"}
            </Button>
          </div>
        </div>
      </div>

      {/* Rules */}
      <div className="px-5 mt-8 max-w-2xl">
        <h2 className="font-display font-bold text-xl">2. Règles d'envoi</h2>
        <div className="mt-4 border border-[var(--border)] p-5 rounded-2xl space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Ton des emails</label>
              <Select value={rules.tone} onValueChange={(v) => setRules({ ...rules, tone: v })}>
                <SelectTrigger className="h-11 rounded-md border-[var(--border)]" data-testid="email-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Formel">Formel</SelectItem>
                  <SelectItem value="Semi-formel">Semi-formel</SelectItem>
                  <SelectItem value="Décontracté">Décontracté</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Délai avant envoi (min)</label>
              <Select value={String(rules.delay_minutes)} onValueChange={(v) => setRules({ ...rules, delay_minutes: +v })}>
                <SelectTrigger className="h-11 rounded-md border-[var(--border)]" data-testid="email-delay"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 minutes (recommandé)</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Limite quotidienne</label>
              <Select value={String(rules.daily_cap)} onValueChange={(v) => setRules({ ...rules, daily_cap: +v })}>
                <SelectTrigger className="h-11 rounded-md border-[var(--border)]" data-testid="email-cap"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 emails / jour</SelectItem>
                  <SelectItem value="10">10 emails / jour</SelectItem>
                  <SelectItem value="20">20 emails / jour (recommandé)</SelectItem>
                  <SelectItem value="50">50 emails / jour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Heures actives</label>
              <div className="flex items-center gap-2 mt-1">
                <Input type="number" min="0" max="23" value={rules.active_hours_start} onChange={(e) => setRules({ ...rules, active_hours_start: +e.target.value })} className="h-11 rounded-md border-[var(--border)] w-20" />
                <span className="text-sm text-zinc-400">à</span>
                <Input type="number" min="0" max="23" value={rules.active_hours_end} onChange={(e) => setRules({ ...rules, active_hours_end: +e.target.value })} className="h-11 rounded-md border-[var(--border)] w-20" />
                <span className="text-xs text-zinc-400 ml-2">UTC</span>
              </div>
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Canaux auto-pilotés</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {[
                { k: "visit", label: "Email après visite" },
                { k: "phone", label: "Email après appel" },
                { k: "linkedin", label: "LinkedIn (bientôt)", dis: true },
                { k: "whatsapp", label: "WhatsApp (bientôt)", dis: true },
              ].map((c) => (
                <button
                  key={c.k}
                  disabled={c.dis}
                  onClick={() => setRules({ ...rules, auto_channels: { ...rules.auto_channels, [c.k]: !rules.auto_channels[c.k] } })}
                  className={`rounded-full border px-4 h-9 text-xs font-semibold ${
                    rules.auto_channels[c.k] ? "bg-emerald-500 text-white border-emerald-500" : "border-[var(--border)] bg-[var(--surface)] hover:bg-black/5"
                  } ${c.dis ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`email-channel-${c.k}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Signature (HTML autorisé)</label>
            <textarea
              value={rules.signature_html}
              onChange={(e) => setRules({ ...rules, signature_html: e.target.value })}
              placeholder="<p><strong>Thomas Martin</strong><br>Rénovation Dubois · 06 12 34 56 78</p>"
              className="mt-1 w-full min-h-[96px] border border-[var(--border)] rounded-md p-3 text-sm font-mono"
              data-testid="email-signature"
            />
          </div>

          <Button onClick={saveRules} disabled={saving} className="rounded-full bg-black text-white h-11 px-5 text-sm" data-testid="email-save-rules">
            {saving ? "…" : "Enregistrer les règles"}
          </Button>
        </div>
      </div>

      {/* Legal */}
      <div className="px-5 mt-8 max-w-2xl pb-16">
        <div className="border border-[var(--border)] p-5 rounded-2xl bg-[var(--surface-alt)]">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-zinc-300 mt-0.5 shrink-0" />
            <div>
              <div className="font-display font-bold text-base">Responsabilité légale</div>
              <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
                En activant l'envoi automatique, vous confirmez que vous êtes responsable des emails envoyés depuis votre adresse. Prosperr agit en tant que sous-traitant technique. Assurez-vous que vos destinataires sont des professionnels B2B et que vos emails respectent la réglementation en vigueur (RGPD, directive ePrivacy). Prosperr ne peut être tenu responsable du contenu des emails envoyés en votre nom.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/emails/history")}
          className="mt-4 text-sm text-zinc-400 hover:text-[var(--text)] underline underline-offset-4"
          data-testid="email-see-history"
        >
          Voir l'historique des envois →
        </button>
      </div>

      {/* Disclaimer modal */}
      {showDisclaimer && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-5" data-testid="email-disclaimer">
          <div className="bg-[var(--surface)] rounded-xl max-w-md w-full p-6">
            <div className="w-12 h-12 rounded-full bg-orange-500/10 grid place-items-center">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <h3 className="mt-4 font-display font-bold text-2xl leading-tight">Confirmez l'activation</h3>
            <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
              Prosperr enverra des emails en votre nom depuis <strong>{cfg.from_email}</strong>. Vous recevrez une notification avant chaque envoi avec la possibilité d'annuler.
            </p>
            <div className="mt-6 flex gap-2">
              <Button onClick={() => setShowDisclaimer(false)} variant="outline" className="flex-1 rounded-full border-[var(--border)] h-11">Annuler</Button>
              <Button onClick={confirmEnable} className="flex-1 rounded-full bg-black text-white h-11" data-testid="email-disclaimer-confirm">
                J'accepte et j'active
              </Button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
